/**
 * Platforms-list panel — the dockable surface that complements the
 * Map view per Flow Q + ADR-024 §2.
 *
 * Stream A PR 5.2's first real consumer of the Zustand selection
 * bus introduced in PR 5.1. Reads the universe of Producers from
 * ``GET /api/query/producers`` (PR 5.2's new endpoint) and renders
 * each as a clickable row. Per the 2026-06-09 two-slot redesign, a
 * row left-click writes ``{kind: "platform", id}`` to the
 * ``inspected`` slot ONLY, which:
 *
 *   - Populates the Entity Details panel (the reading focus).
 *   - Highlights + scrolls this row.
 *
 * It deliberately does NOT move the Map ring/trail — that is the
 * intended divergence (the operator can skim rows without yanking the
 * camera). Pushing a row to the Map is the separate Phase-2 "Select on
 * Map" affordance.
 *
 * # Scope (Flow 05 Frame 1 per-row content)
 *
 *   - Flat list ordered by display_name ascending (server-side
 *     sort matches the Flow 05 §"Frame 1" default).
 *   - Per-row content per Flow 05 Frame 1: **status dot**, **ID**
 *     (display_name, mono), **type** (SAPIENT),
 *     **last-good** timestamp, **status-rate** (msgs/s over the last
 *     60 s) and **detection-rate** (msgs/s over the last 60 s). Built by
 *     merging two sources on ``producer_id``:
 *       - :func:`useProducers` — slow-moving metadata (ID, type).
 *       - :func:`useProducersStatus` — fast-moving live status
 *         (health_state, last_status_time, 60 s counts). Per Flow 05
 *         §"Behaviour notes" the BFF computes the *counts*; the UI
 *         only divides count ÷ window_seconds for the rate label.
 *     A Producer present in metadata but absent from status
 *     (registered but silent) renders gracefully: 'unknown' dot,
 *     em-dash last-good, zero rates.
 *   - Selection-on-click inspects the platform (``inspected`` slot).
 *   - Inspected row gains a left-edge action-color highlight per
 *     Flow Q §"Frame 2" + §"Frame 3" — wired via
 *     :func:`useIsInspected` from the selection store.
 *
 * # Out of scope (separate tracked follow-ups)
 *
 *   - Aggregator grouping (Flow 05 §"Frame 1" groups platforms
 *     under their parent aggregator's collapsible row).
 *   - Status-priority sort (offline → degraded → unknown → healthy).
 *   - Filter bar / search box / sort dropdown / summary line
 *     (Flow 05 §"Frame 1" + §"Frame 4").
 *   - Stale-stream indicators on the rate columns (Flow 05 §"Edge
 *     cases" — needs the SSE connection state).
 *   - SSE in-place merge + the 240 ms transition flash (Flow 05
 *     §"Behaviour notes"). This PR uses a TanStack Query refetch
 *     interval (see :func:`useProducersStatus`) for liveness.
 *   - Converting the flat list to a ``<table>`` / ``<thead>``
 *     (Flow 05 §"Accessibility notes").
 *
 * # "Select on Map" (the 2026-06-09 two-slot redesign, Phase 2)
 *
 * Each row is wrapped with :func:`SelectOnMapContextMenu` — right-click /
 * the context-menu key / ``Shift+F10`` opens a one-item menu that pushes
 * the platform to the map (both selection slots + a force-recenter so the
 * camera eases to the marker even if it's already in view). This is the
 * explicit push-to-map gesture that complements the divergent left-click
 * (``inspect`` only). Aggregator grouping is still out of scope here, so
 * every row is a platform and offers the affordance.
 *
 * # Cross-references
 *
 *   - REQ-UI-007 (entity details list rendering — the row content).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-UI-046 (status colors survive both themes — the dot).
 *   - REQ-UI-051 (Platforms panel as a selection-bus originator).
 *   - REQ-UI-059 (a11y — keyboard navigation across rows).
 *   - Flow 05 §"Frame 1" (per-row content schema) + §"Behaviour
 *     notes" (BFF-computed counts).
 *   - Flow Q §"Frame 1" + §"Frame 2" + §"Frame 3"
 *     (selection-bus integration).
 *   - ADR-024 §2 (Platforms dockable surface) + §5 (selection
 *     contract).
 *
 * # Behavior in selection-bus integration
 *
 * The panel reads + writes the ``inspected`` slot (the 2026-06-09
 * two-slot redesign):
 *
 *   - **Click → inspect.** Clicking a row sets ``inspected`` only;
 *     Entity Details populates and this row highlights. The Map ring is
 *     intentionally left where it was.
 *   - **Map → row highlight.** When the Map's marker-click writes a
 *     selection (which sets BOTH slots), the corresponding row here gains
 *     a left-edge action-color highlight via :func:`useIsInspected`. This
 *     is the Flow Q §"Frame 2" + §"Frame 3" behaviour.
 *
 * Per Flow Q §"Behavior notes" the list also keeps the selected row in
 * view: :func:`useKeepSelectedRowInView` centres an off-screen selection
 * and holds a selected row in place as rows stream in, and the header's
 * :func:`ListFollowToggle` decides what happens with no selection
 * (REQ-UI-182). Positioning is instant since 2026-10-01 — the 160 ms CSS
 * ease made held rows lurch under live updates (see the hook's docstring).
 */
import { memo, useCallback, useMemo, useRef } from "react";
import {
  useSelectionStore,
  useIsInspected,
  useInspectedIdForKind,
  useMapSelectedIdForKind,
} from "@/selection/selection-store";
import { SelectOnMapContextMenu } from "@/selection/select-on-map-context-menu";
import {
  type PanelVisibilityApi,
  useKeepSelectedRowInView,
} from "@/selection/use-keep-selected-row-in-view";
import { useListFollow } from "@/multi-pane/list-follow-store";
import { ListFollowToggle } from "@/multi-pane/list-follow-toggle";
import { usePausedListRows } from "@/multi-pane/use-paused-list-rows";
import { useProducers, type ProducerListItem } from "@/platforms/use-producers";
import { useProducersStatus, type ProducerStatusItem } from "@/platforms/use-producers-status";
import { getStatusColor, healthStateToMarkerStatus } from "@/map/marker-styling";
import { usePreferences } from "@/preferences/preferences-context";
import { PLATFORM_DEFAULT_SORT, sortPlatformRows } from "@/platforms/platform-sort";

/**
 * Render the Platforms-list panel.
 *
 * Mounted by :file:`@/multi-pane/panel-registry.tsx` against the
 * ``platforms`` :type:`PanelId`. The component is a full-height
 * scrollable region; dockview owns the surrounding chrome (tab
 * header, drag handle, resize splits).
 */
interface PlatformsPanelProps {
  /**
   * dockview panel api, threaded by :func:`PlatformsDockPanel`. Lets the
   * keep-in-view hook re-centre the selected row when this tab is shown
   * after a Map selection made while it was hidden (REQ-UI-051; PR #300
   * item 2). Optional so the panel renders standalone in tests.
   */
  panelApi?: PanelVisibilityApi;
}

/** A platform row's stable identity for the pause snapshot. */
const platformRowId = (item: ProducerListItem): string => item.producer_id;

export function PlatformsPanel({ panelApi }: PlatformsPanelProps = {}): React.ReactElement {
  const query = useProducers();
  // Live-status source (status dot / last-good / 60 s rates). This
  // is supplementary: the metadata query owns the loading/empty/
  // error gating because it defines the *universe* of platforms.
  // A missing / in-flight / errored status query simply yields
  // 'unknown' rows rather than blanking the panel — operator-
  // visibility per CLAUDE.md §8.1 (show partial data, not nothing).
  const statusQuery = useProducersStatus();
  // A list-row left-click sets the ``inspected`` slot ONLY (the 2026-06-09
  // two-slot redesign): Entity Details + this list's highlight move, but
  // the map ring/trail stay put. Pushing a row to the map is the separate
  // Phase-2 "Select on Map" affordance. A second left-click on the row that
  // is already inspected deselects it — both slots clear, the camera stays
  // (REQ-UI-051 amended 2026-10-01).
  const toggleInspect = useSelectionStore((s) => s.toggleInspect);

  // Stable row-select handler so the memoised :func:`PlatformRow`s
  // don't re-render when the panel re-renders on an inventory poll.
  // ``inspect`` is a stable Zustand action reference.
  const handleSelect = useCallback(
    (producerId: string) => toggleInspect({ kind: "platform", id: producerId }),
    [toggleInspect],
  );

  // Memoised so the ``?? []`` fallback doesn't hand a fresh array
  // reference to the keep-selected-in-view effect's deps each render.
  // Structural sharing on the producers query (TanStack default) keeps
  // ``query.data`` referentially stable across polls that return an
  // unchanged inventory, so ``items`` only changes when a producer is
  // actually added or removed — no per-poll re-render churn.
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const windowSeconds = statusQuery.data?.window_seconds ?? 60;

  // Index the live-status items by producer_id for an O(1) merge in
  // the row render. Recomputed only when the status payload changes.
  const statusByProducer = useMemo(() => {
    const map = new Map<string, ProducerStatusItem>();
    for (const s of statusQuery.data?.items ?? []) {
      map.set(s.producer_id, s);
    }
    return map;
  }, [statusQuery.data]);

  // The operator's per-panel sort selection (REQ-UI-110), persisted in the
  // working-state / Profile ``list_sort``; absent ⇒ the panel default
  // (display name ascending). The gear menu (in the dockview header) writes it;
  // this body reads it from the SAME working state, so the list re-orders the
  // instant the operator changes the selection — no prop bridge across the
  // dockview content/header boundary.
  const sortSelection = usePreferences().workingState.list_sort?.platforms ?? PLATFORM_DEFAULT_SORT;

  // Sort a COPY (sortBy is non-mutating) by the selection, joining live status
  // for the status-derived fields. Recomputed only when the inventory, the
  // status payload, or the selection changes. Sorting is on row identity (ids
  // are unchanged), so it never disturbs the id-keyed selection or the
  // keep-in-view anchor (REQ-UI-051).
  const sortedItems = useMemo(
    () => sortPlatformRows(items, statusByProducer, sortSelection),
    [items, statusByProducer, sortSelection],
  );

  // The list highlights + scrolls to the INSPECTED platform (reading
  // focus), independent of the map ring (the two-slot redesign).
  const selectedPlatformId = useInspectedIdForKind("platform");
  // The map slot too, only so a platform selected on the map is admitted to a
  // paused list and therefore has a row to reveal (REQ-UI-051).
  const mapSelectedPlatformId = useMapSelectedIdForKind("platform");
  const listRef = useRef<HTMLUListElement | null>(null);

  // What the list renders: the live sorted rows while following, the rows
  // frozen at the flip while not — this list is status-sorted, so a platform
  // changing health re-orders it under the operator (REQ-UI-182 amended
  // 2026-10-10). Each held row still shows its CURRENT status: the freeze is
  // on the row set and its order, never on the values (``use-paused-list-rows``).
  const follow = useListFollow("platforms");
  const { rows: visibleItems, withheld } = usePausedListRows({
    rows: sortedItems,
    follow,
    idOf: platformRowId,
    admit: [selectedPlatformId, mapSelectedPlatformId],
  });

  // Keep the selected row visible: centre it on selection / when the tab is
  // shown, and hold it steady as the status list refetches. Keyed on the
  // RENDERED list so the anchor logic sees the order the operator sees
  // (REQ-UI-051; ADR-024 §5; storyboard Flow Q §"Frame 2").
  useKeepSelectedRowInView(listRef, selectedPlatformId, visibleItems, panelApi, { follow });

  if (query.isLoading) {
    return <LoadingState />;
  }
  if (query.isError) {
    return <ErrorState message={query.error.message} onRetry={() => query.refetch()} />;
  }
  // Gated on the rendered rows: a list paused while empty stays empty.
  if (visibleItems.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      data-testid="platforms-panel"
      role="region"
      aria-label="Connected platforms"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="shrink-0 whitespace-nowrap text-body font-medium text-text-primary">
          Platforms <span className="ml-2 text-body-sm text-text-muted">{visibleItems.length}</span>
        </h2>
        <ListFollowToggle panelId="platforms" withheld={withheld} />
      </header>
      <ul
        ref={listRef}
        // ``overscroll-contain``: a wheel that reaches this list's end must not
        // continue into dockview's own ``.dv-view`` — an ``overflow-y: auto``
        // wrapper around the whole group, tab strip included — which scrolls the
        // panel's tab up under the top nav bar while the page itself never moves
        // (measured on Selection Details 2026-10-10: ``.dv-view`` scrollTop
        // 0 → 605, group y 48 → -557). No list panel can reach that state today
        // (``.dv-view`` has no scrollable extent around one — probed in both
        // directions at two viewport heights), but the end of a list is now a
        // place operators sit: a paused list stops moving, so they scroll to the
        // bottom and keep going (REQ-UI-182 amended 2026-10-10).
        className="flex-1 overflow-y-auto overscroll-contain"
        role="list"
        aria-label="Platform list"
      >
        {visibleItems.map((item) => (
          <PlatformRow
            key={item.producer_id}
            item={item}
            status={statusByProducer.get(item.producer_id) ?? null}
            windowSeconds={windowSeconds}
            onSelect={handleSelect}
          />
        ))}
      </ul>
    </div>
  );
}

interface PlatformRowProps {
  item: ProducerListItem;
  /** Live-status item merged on ``producer_id``, or null when the
   *  Producer is registered but silent (no time-series data). */
  status: ProducerStatusItem | null;
  /** Count-window length (seconds) for the rate columns. */
  windowSeconds: number;
  onSelect: (producerId: string) => void;
}

/**
 * A single platform row.
 *
 * ``memo``-wrapped so a panel re-render (e.g. the 5 s inventory poll,
 * or another row's status changing) doesn't re-render every row. With
 * structural sharing the ``item`` and ``status`` props are
 * referentially stable unless that producer's metadata / status
 * actually changed, and ``onSelect`` is a stable callback — so a row
 * re-renders only when its own data changes. This is what keeps the
 * "last-good" / rate text from churning on every poll. Selection state
 * is read via the :func:`useIsInspected` Zustand selector (not a prop),
 * so a selection change re-renders only the two affected rows.
 */
const PlatformRow = memo(function PlatformRow({
  item,
  status,
  windowSeconds,
  onSelect,
}: PlatformRowProps): React.ReactElement {
  const isSelected = useIsInspected("platform", item.producer_id);

  // Scroll-into-view on selection / show / data-update is handled at the
  // panel level (see PlatformsPanel) so the selected row stays anchored
  // as the list updates — mirrors the Detections panel. Positioning is
  // instant (see ``useKeepSelectedRowInView``'s docstring for why).

  return (
    // Right-click / context-menu key offers "Select on Map" — push this
    // platform to the map (both slots + force-recenter). Platforms always
    // have a map marker (aggregator rows are out of scope here), so the
    // affordance is always enabled; the layer no-ops the recenter for the
    // rare registered-but-silent platform with no snapshot position.
    <SelectOnMapContextMenu value={{ kind: "platform", id: item.producer_id }}>
      <li
        // Tabindex 0 makes each row keyboard-focusable per
        // REQ-UI-059. Space/Enter handler below maps keyboard
        // selection to the same select() call as the click handler.
        tabIndex={0}
        role="listitem"
        aria-selected={isSelected}
        data-row-id={item.producer_id}
        data-testid={`platform-row-${item.producer_id}`}
        onClick={() => onSelect(item.producer_id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(item.producer_id);
          }
        }}
        className={[
          "group relative cursor-pointer border-b border-border px-4 py-3",
          "transition-colors duration-100",
          "hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
          isSelected ? "bg-bg-elevated" : "",
        ].join(" ")}
      >
        {/* Left-edge action-color highlight bar when selected (per
         * Flow Q §"Frame 2"). 3px-wide vertical stripe in the
         * brand-accent color. */}
        <span
          aria-hidden="true"
          className={[
            "absolute left-0 top-0 h-full w-[3px]",
            "transition-colors duration-100",
            isSelected ? "bg-brand" : "bg-transparent",
          ].join(" ")}
        />
        {/* Flow 05 Frame 1 per-row content: status dot + ID + type on
         * the first line; last-good + 60 s rates on the second. */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <StatusDot status={status} />
            <span className="font-mono text-body text-text-primary" data-testid="platform-row-name">
              {item.display_name}
            </span>
            <span
              // ``text-text-secondary`` (not ``-muted``): the SAPIENT type
              // is load-bearing row content per Flow 05
              // Frame 1, so it must clear WCAG AA 4.5:1 in both themes
              // (REQ-UI-040). The muted token measured ~2.8:1 against
              // the panel bg in both palettes; secondary clears AA
              // (~6.5:1 light / ~8.9:1 dark) at the same de-emphasised
              // visual weight.
              className="ml-auto text-caption font-medium uppercase tracking-wide text-text-secondary"
              data-testid="platform-row-type"
            >
              {formatProducerType(item.producer_type)}
            </span>
          </div>
          <div className="flex items-baseline gap-3 text-caption text-text-secondary">
            {/* ``tabular-nums`` keeps the relative-age digits a fixed
             * width so the text doesn't reflow as it ticks; the rates
             * block is pinned right (``ml-auto``) so a width change in
             * "last-good" never nudges a sibling. */}
            <span
              className="tabular-nums"
              data-testid="platform-row-last-good"
              title="Last good status message"
            >
              {formatLastGood(status?.last_status_time ?? null)}
            </span>
            <span className="ml-auto flex items-baseline gap-3 font-mono tabular-nums">
              <span data-testid="platform-row-status-rate" title="Status messages / s (last 60 s)">
                {formatRate(status?.status_count ?? 0, windowSeconds)} status/s
              </span>
              <span
                data-testid="platform-row-detection-rate"
                title="Detection messages / s (last 60 s)"
              >
                {formatRate(status?.detection_count ?? 0, windowSeconds)} detection/s
              </span>
            </span>
          </div>
        </div>
      </li>
    </SelectOnMapContextMenu>
  );
});

/**
 * Status dot for a platform row.
 *
 * Reuses the Map's ``health_state`` → category mapping and the
 * theme-aware status color (REQ-UI-046 — status colors survive both
 * themes) so the row dot agrees with the Map marker and the Entity
 * Details STATUS pill. A null ``status`` (registered-but-silent
 * Producer) maps to the proto default (UNSPECIFIED → 'unknown'),
 * which renders the grey 'unknown' dot.
 *
 * Per Flow 05 §"Accessibility notes" the color carries an
 * ``aria-label`` text companion so colorblind operators have a
 * non-color signal.
 */
function StatusDot({ status }: { status: ProducerStatusItem | null }): React.ReactElement {
  const marker = healthStateToMarkerStatus(status?.health_state ?? 0);
  return (
    <span
      className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10 dark:ring-white/10"
      style={{ backgroundColor: getStatusColor(marker) }}
      role="img"
      aria-label={`Status: ${marker}`}
      data-testid="platform-row-status-dot"
      data-status={marker}
    />
  );
}

/** Uppercase the SAPIENT taxonomy label (2026-05-28). */
function formatProducerType(type: ProducerListItem["producer_type"]): string {
  return type.toUpperCase();
}

/**
 * Compact "last good" rendering from an ISO timestamp.
 *
 * Renders a relative age for recent times ("73s ago", "4m ago") and
 * a short UTC clock time for older ones, so the operator reads
 * freshness at a glance. Null (registered-but-silent Producer) →
 * em-dash.
 */
function formatLastGood(iso: string | null): string {
  if (iso === null) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  const ageMs = Date.now() - parsed.getTime();
  if (ageMs < 0) {
    // Clock skew / future-dated — fall back to the UTC time.
    return utcClock(parsed);
  }
  const ageSec = Math.floor(ageMs / 1000);
  if (ageSec < 60) return `${ageSec}s ago`;
  const ageMin = Math.floor(ageSec / 60);
  if (ageMin < 60) return `${ageMin}m ago`;
  // Older than an hour: show a short UTC clock so operators can
  // cross-reference logs without ambiguity.
  return `${utcClock(parsed)} UTC`;
}

function utcClock(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/**
 * Compute the per-second rate from a window count, formatted to one
 * decimal place.
 *
 * Per Flow 05 §"Behaviour notes" the BFF supplies the *count* over
 * ``windowSeconds``; the only browser-side arithmetic is the
 * count ÷ window division for the display label.
 */
function formatRate(count: number, windowSeconds: number): string {
  if (windowSeconds <= 0) return "0.0";
  return (count / windowSeconds).toFixed(1);
}

// ---- Render-state stubs ------------------------------------------

function LoadingState(): React.ReactElement {
  return (
    <div
      data-testid="platforms-panel-loading"
      role="status"
      aria-label="Loading platforms"
      className="flex h-full w-full items-center justify-center bg-bg-base text-text-secondary"
    >
      <p className="text-body-sm">Loading platforms…</p>
    </div>
  );
}

function EmptyState(): React.ReactElement {
  return (
    <div
      data-testid="platforms-panel-empty"
      role="region"
      aria-label="No platforms"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      <p className="text-body text-text-primary">No registered platforms</p>
      <p className="text-body-sm text-text-muted">
        Producers register via messages once connected.
      </p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="platforms-panel-error"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-bg-base"
    >
      <p className="text-body text-destructive">Could not load platforms</p>
      <p className="px-6 text-center text-body-sm text-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
      >
        Retry
      </button>
    </div>
  );
}
